import type { UpgradeFeature } from '@/atoms/upgradeModalAtom';
import AlertEn from '@/assets/upgrade-modal/alert-en.jpg';
import AlertJa from '@/assets/upgrade-modal/alert-ja.jpg';
import CsvDownloadEn from '@/assets/upgrade-modal/csv-download-en.jpg';
import CsvDownloadJa from '@/assets/upgrade-modal/csv-download-ja.jpg';
import SavedFilterEn from '@/assets/upgrade-modal/saved-filter-en.mp4';
import SavedFilterJa from '@/assets/upgrade-modal/saved-filter-ja.mp4';
import ThemesEn from '@/assets/upgrade-modal/themes-en.jpg';
import ThemesJa from '@/assets/upgrade-modal/themes-ja.jpg';
import TransitionEn from '@/assets/upgrade-modal/transition-en.jpg';
import TransitionJa from '@/assets/upgrade-modal/transition-ja.jpg';
import TreeMapEn from '@/assets/upgrade-modal/treemap-en.mp4';
import TreeMapJa from '@/assets/upgrade-modal/treemap-ja.mp4';
import RWBizLogo from '@/assets/rw-biz-logo.png';
import RWPlusLogo from '@/assets/rw-plus-logo.png';
import { Dialog } from '@/components/Dialog/Dialog';
import { useUpgradeModal } from '@/hooks/useUpgradeModal';
import {
  IconChevronLeft,
  IconChevronRight,
  IconCreditCard,
  IconExternalLink,
  IconFileDownload,
  IconFilePlus,
  IconHash,
  IconTags,
  IconX,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type FeatureShowcase = {
  key: UpgradeFeature;
  titleKey: string;
  descriptionKey: string;
  media: {
    type: 'image' | 'video';
    src: Record<'en' | 'ja', string>;
    positionClass?: 'object-bottom' | 'object-top';
  };
};

const FEATURE_SHOWCASES: FeatureShowcase[] = [
  {
    key: 'csv-download',
    titleKey: 'CSV Download',
    descriptionKey:
      '検索結果をCSVで書き出し、詳しい分析やレポート作成、共有に活用できます。Plusでは毎月最大3,000件の記事をダウンロードできます。',
    media: {
      type: 'image',
      src: { en: CsvDownloadEn, ja: CsvDownloadJa },
    },
  },
  {
    key: 'treemap',
    titleKey: 'TreeMap',
    descriptionKey:
      '検索結果を形づくるトピックをひと目で把握できます。記事のまとまりを大きさと色で探索し、主要なテーマや新たな傾向を発見できます。',
    media: {
      type: 'video',
      src: { en: TreeMapEn, ja: TreeMapJa },
      positionClass: 'object-bottom',
    },
  },
  {
    key: 'transition',
    titleKey: 'Transition',
    descriptionKey:
      '注目度の変化を時系列で追跡できます。期間ごとのトピックを比較し、上昇中・減少中・継続中の傾向を把握できます。',
    media: {
      type: 'image',
      src: { en: TransitionEn, ja: TransitionJa },
    },
  },
  {
    key: 'saved-filter',
    titleKey: '保存フィルター',
    descriptionKey:
      'よく使う検索条件を保存し、すべての設定を保ったままワンクリックで呼び出せます。',
    media: {
      type: 'video',
      src: { en: SavedFilterEn, ja: SavedFilterJa },
      positionClass: 'object-top',
    },
  },
  {
    key: 'alert',
    titleKey: 'アラート',
    descriptionKey:
      '保存したフィルターをメールアラートに設定し、調査条件に合う新着記事をすぐに確認できます。',
    media: {
      type: 'image',
      src: { en: AlertEn, ja: AlertJa },
    },
  },
  {
    key: 'themes',
    titleKey: 'テーマ',
    descriptionKey:
      '厳選された5つの追加テーマを利用して調査範囲を広げ、より多くの政策分野を横断するつながりを発見できます。',
    media: {
      type: 'image',
      src: { en: ThemesEn, ja: ThemesJa },
    },
  },
];

const getFeatureIndex = (feature: UpgradeFeature) =>
  Math.max(
    FEATURE_SHOWCASES.findIndex((item) => item.key === feature),
    0
  );

const getPricingUrl = (language: 'en' | 'ja') =>
  language === 'ja'
    ? 'https://www.osintech.net/rulewatcher-price-plan-table'
    : 'https://web.rulewatcher.com/pricing';

const BIZ_GRADIENT_STYLE = {
  backgroundColor: '#07002E',
  backgroundImage: 'linear-gradient(275deg, #07002E 0%, #002455 100%)',
};

const BIZ_FEATURES = [
  {
    key: 'themes',
    titleKey: 'biz. 限定テーマ4種',
    descriptionKey:
      '組織向けの4つの限定テーマで、さらに専門的な領域まで調査範囲を広げられます。',
    Icon: IconHash,
  },
  {
    key: 'custom-tags',
    titleKey: 'カスタムタグ作成',
    descriptionKey: '組織の調査課題に合わせて、独自のタグの作成依頼が行えます',
    Icon: IconTags,
  },
  {
    key: 'source-requests',
    titleKey: '情報ソース追加リクエスト',
    descriptionKey:
      '調査対象に加えたい媒体や情報ソースの追加をリクエストできます。',
    Icon: IconFilePlus,
  },
  {
    key: 'csv-downloads',
    titleKey: 'CSVダウンロード 10,000件',
    descriptionKey: '検索結果から毎月最大10,000件の記事をCSVで書き出せます。',
    Icon: IconFileDownload,
  },
  {
    key: 'payment',
    titleKey: 'クレジットカード・請求書払い',
    descriptionKey:
      'クレジットカードまたは請求書から、組織に合った支払い方法を選べます。',
    Icon: IconCreditCard,
  },
] as const;

type UpgradeModalShowcaseProps = {
  initialFeature: UpgradeFeature;
  onDismiss: () => void;
};

export const UpgradeModalShowcase = ({
  initialFeature,
  onDismiss,
}: UpgradeModalShowcaseProps) => {
  const { t, i18n } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(() =>
    getFeatureIndex(initialFeature)
  );
  const feature = FEATURE_SHOWCASES[activeIndex];
  const language = (i18n.resolvedLanguage ?? i18n.language).startsWith('ja')
    ? 'ja'
    : 'en';
  const pricingUrl = getPricingUrl(language);
  const mediaSrc = feature.media.src[language];

  const selectRelativeFeature = (offset: number) => {
    setActiveIndex(
      (current) =>
        (current + offset + FEATURE_SHOWCASES.length) % FEATURE_SHOWCASES.length
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectRelativeFeature(-1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectRelativeFeature(1);
    }
  };

  return (
    <div
      className="relative flex w-full flex-col items-center gap-8 rounded-[32px] bg-white px-6 py-8 text-rw-neutral-rich-black sm:px-12 sm:py-[35px]"
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-5 top-5 z-10 grid size-10 place-items-center rounded-full text-rw-neutral-dark-gray transition-colors hover:bg-rw-neutral-lighter-gray focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rw-sub sm:right-8 sm:top-8"
        aria-label={t('案内を閉じる')}
      >
        <IconX className="size-6" strokeWidth={2} aria-hidden="true" />
      </button>

      <header className="flex w-full max-w-[467px] flex-col items-center gap-4 px-8 text-center sm:px-0">
        <img
          src={RWPlusLogo}
          alt="RuleWatcher Plus"
          className="h-auto w-full max-w-[415px]"
          loading="lazy"
          decoding="async"
        />
        <p className="text-base leading-6 text-rw-neutral-off-black">
          {t('以下の RuleWatcher Plus 機能でリサーチを強化しましょう。')}
        </p>
      </header>

      <section
        className="flex w-full flex-col gap-5"
        aria-live="polite"
        aria-label={t('RuleWatcher Plus の機能紹介')}
      >
        <div className="space-y-1 text-left">
          <h2 className="text-2xl font-semibold leading-8">
            {t(feature.titleKey)}
          </h2>
          <p className="min-h-[42px] text-sm leading-[21px] text-rw-neutral-rich-black">
            {t(feature.descriptionKey)}
          </p>
        </div>

        <div className="flex w-full items-center gap-2 overflow-hidden rounded-xl">
          <button
            type="button"
            onClick={() => selectRelativeFeature(-1)}
            className="grid size-8 shrink-0 place-items-center rounded-full text-rw-neutral-dark-gray transition-colors hover:bg-rw-neutral-lighter-gray focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rw-sub"
            aria-label={t('前のPlus機能を表示')}
          >
            <IconChevronLeft className="size-4" strokeWidth={2} />
          </button>

          <div className="aspect-video min-w-0 flex-1 overflow-hidden rounded-xl bg-rw-neutral-off-white">
            {feature.media.type === 'video' ? (
              <video
                key={mediaSrc}
                src={mediaSrc}
                aria-label={t('{{feature}}の紹介画像', {
                  feature: t(feature.titleKey),
                })}
                className={`size-full object-cover ${feature.media.positionClass ?? ''}`}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <img
                src={mediaSrc}
                alt={t('{{feature}}の紹介画像', {
                  feature: t(feature.titleKey),
                })}
                className="size-full object-cover"
                decoding="async"
              />
            )}
          </div>

          <button
            type="button"
            onClick={() => selectRelativeFeature(1)}
            className="grid size-8 shrink-0 place-items-center rounded-full text-rw-neutral-dark-gray transition-colors hover:bg-rw-neutral-lighter-gray focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rw-sub"
            aria-label={t('次のPlus機能を表示')}
          >
            <IconChevronRight className="size-4" strokeWidth={2} />
          </button>
        </div>

        <div className="flex items-center justify-center gap-0.5">
          {FEATURE_SHOWCASES.map((item, index) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="grid size-6 place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rw-sub"
              aria-label={t('{{feature}}を表示', {
                feature: t(item.titleKey),
              })}
              aria-current={index === activeIndex ? 'true' : undefined}
            >
              <span
                aria-hidden="true"
                className={`size-1.5 rounded-full transition-colors ${
                  index === activeIndex
                    ? 'bg-rw-neutral-dark-gray'
                    : 'bg-rw-neutral-lighter-gray'
                }`}
              />
            </button>
          ))}
        </div>
      </section>

      <footer className="flex w-full flex-col items-center gap-4 text-center">
        <div className="flex flex-col items-center gap-1">
          <p className="text-base font-semibold leading-6 text-rw-neutral-dark-gray">
            {t('無料トライアルあり')}
          </p>
          <a
            href={pricingUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-12 items-center justify-center gap-1 rounded-full bg-gradient-to-r from-gradient-teal-end to-gradient-teal-start px-5 py-2 text-center text-lg font-semibold leading-7 text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rw-sub sm:text-2xl sm:leading-8"
          >
            {t('ウェブサイトで詳細を見る')}
            <IconExternalLink className="size-5 sm:size-6" strokeWidth={2} />
          </a>
        </div>
      </footer>
    </div>
  );
};

type BusinessUpgradeModalShowcaseProps = {
  onDismiss: () => void;
};

export const BusinessUpgradeModalShowcase = ({
  onDismiss,
}: BusinessUpgradeModalShowcaseProps) => {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage ?? i18n.language).startsWith('ja')
    ? 'ja'
    : 'en';
  const pricingUrl = getPricingUrl(language);

  return (
    <div className="relative flex w-full flex-col items-center gap-8 rounded-[32px] bg-white px-6 py-8 text-[#07002E] sm:min-h-[744px] sm:px-12 sm:py-[35px]">
      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-5 top-5 z-10 grid size-10 place-items-center rounded-full text-[#07002E] transition-colors hover:bg-rw-neutral-lighter-gray focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#07002E] sm:right-8 sm:top-8"
        aria-label={t('案内を閉じる')}
      >
        <IconX className="size-6" strokeWidth={2} aria-hidden="true" />
      </button>

      <header className="flex w-full max-w-[467px] flex-col items-center gap-4 px-8 text-center sm:px-0">
        <img
          src={RWBizLogo}
          alt="RuleWatcher biz."
          className="h-auto w-full max-w-[415px]"
          loading="lazy"
          decoding="async"
        />
        <p className="text-base leading-6 text-[#07002E]">
          {t(
            'RuleWatcher biz. の機能で、組織のリサーチをさらに強化しましょう。'
          )}
        </p>
      </header>

      <section
        className="w-full rounded-[20px] p-[2px]"
        style={BIZ_GRADIENT_STYLE}
        aria-label={t('RuleWatcher biz. の機能一覧')}
      >
        <div className="divide-y divide-rw-neutral-lighter-gray overflow-hidden rounded-[18px] bg-white">
          {BIZ_FEATURES.map(({ key, titleKey, descriptionKey, Icon }) => (
            <div
              key={key}
              data-biz-feature={key}
              className="flex min-h-[76px] items-center gap-4 px-4 py-3 text-left sm:px-5"
            >
              <span
                className="grid size-11 shrink-0 place-items-center rounded-full text-white"
                style={BIZ_GRADIENT_STYLE}
                aria-hidden="true"
              >
                <Icon className="size-6" strokeWidth={1.8} />
              </span>
              <span className="min-w-0 space-y-0.5">
                <span className="block text-base font-semibold leading-6 text-[#07002E]">
                  {t(titleKey)}
                </span>
                <span className="block text-sm leading-5 text-[#07002E]">
                  {t(descriptionKey)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </section>

      <footer className="mt-auto flex w-full flex-col items-center gap-4 text-center">
        <div className="flex flex-col items-center gap-1">
          <p className="text-base font-semibold leading-6 text-[#07002E]">
            {t('無料トライアルあり')}
          </p>
          <a
            href={pricingUrl}
            target="_blank"
            rel="noreferrer"
            style={BIZ_GRADIENT_STYLE}
            className="inline-flex min-h-12 items-center justify-center gap-1 rounded-full px-5 py-2 text-center text-lg font-semibold leading-7 text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#07002E] sm:text-2xl sm:leading-8"
          >
            {t('ウェブサイトで詳細を見る')}
            <IconExternalLink className="size-5 sm:size-6" strokeWidth={2} />
          </a>
        </div>
      </footer>
    </div>
  );
};

export const UpgradeModal = () => {
  const { t } = useTranslation();
  const { isOpen, feature, tier, close } = useUpgradeModal();
  const isBusiness = tier === 'business';

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      showCloseButton={false}
      maxWidth="plus"
      panelClassName="!rounded-[32px] !p-0"
      ariaLabel={t(
        isBusiness
          ? 'RuleWatcher biz. の機能紹介'
          : 'RuleWatcher Plus の機能紹介'
      )}
    >
      {isBusiness ? (
        <BusinessUpgradeModalShowcase onDismiss={close} />
      ) : (
        <UpgradeModalShowcase
          key={feature}
          initialFeature={feature}
          onDismiss={close}
        />
      )}
    </Dialog>
  );
};
